import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  // Refs are no longer marked required: snippets/background-media.liquid has been
  // customized to render a <video data-video-type="native"> with <source data-sourcesrc>
  // children that are loaded by the deferred-video hydrator in assets/custom.js.
  // The original ref="videoElement"/ref="videoSources[]" attributes were dropped in
  // that template, so this component now activates over markup it doesn't own. We
  // still want it present for the CSS (.group-block:has(> video-background-component)
  // in base.css), but its connectedCallback must no-op when its refs aren't there
  // instead of throwing MissingRefError and tearing down the page.

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;
    if (!videoSources || !videoElement) return;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
